<template>
    <form @submit.prevent="submit" class="login-form-container">
        <!-- Logo/App Icon - Positioned to protrude from top -->
        <div class="logo-container">
            <img
                :src="$store.state.settings.icon"
                alt="App Icon"
                class="logo-image"
            />
        </div>

        <!-- Welcome Header -->
        <div class="text-center mb-6 mt-16">
            <h1 class="text-2xl font-bold text-gray-800">
                {{ welcomeTitle }}
            </h1>
            <p class="text-sm text-gray-600 mt-1">
                {{ welcomeSubtitle }}
            </p>
        </div>

        <!-- Account Field -->
        <div class="mb-3">
            <label class="block text-sm font-medium text-gray-700 mb-1" for="email">
                {{ $t('Account') }}
            </label>
            <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                </div>
                <input
                    id="email"
                    v-model="user.email"
                    type="email"
                    class="form-input w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    :placeholder="$t('Enter your account ID')"
                    required
                />
            </div>
        </div>

        <!-- Password Field with Show/Hide Toggle -->
        <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1" for="password">
                {{ $t('Password') }}
            </label>
            <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                </div>
                <input
                    id="password"
                    v-model="user.password"
                    :type="showPassword ? 'text' : 'password'"
                    class="form-input w-full border border-gray-300 rounded-lg pl-10 pr-12 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    :placeholder="$t('Enter your password')"
                    required
                />
                <button
                    type="button"
                    @click="showPassword = !showPassword"
                    class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                    <svg v-if="showPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                    <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.543 7-1.275 4.057-5.065 7-9.543 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                </button>
            </div>
        </div>

        <!-- Remember Me Checkbox -->
        <div class="mb-5 flex items-center">
            <input
                id="remember"
                v-model="user.remember"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label for="remember" class="ml-2 block text-sm text-gray-700">
                {{ $t('Remember me') }}
            </label>
        </div>

        <!-- Submit Button -->
        <button
            id="submit-login"
            type="submit"
            class="w-full bg-blue-900 hover:bg-blue-800 text-white font-bold py-2.5 px-4 rounded-lg transition duration-200 focus:outline-none focus:shadow-outline"
            data-style="zoom-in"
        >
            <span v-if="!isLoading">{{ buttonText }}</span>
            <span v-else class="flex items-center justify-center">
                <svg class="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ $t('Loading...') }}
            </span>
        </button>

        <!-- Links Section -->
        <div class="mt-5">
            <!-- Divider -->
            <div class="relative flex items-center justify-center mb-3">
                <div class="absolute border-t border-gray-300 w-full"></div>
                <div class="relative bg-white px-4 text-sm text-gray-500">
                    {{ $t('or') }}
                </div>
            </div>

            <!-- Admin Contact Link -->
            <div class="text-center text-sm text-gray-600">
                {{ $t('Don\'t have an account?') }}
                <a
                    href="#"
                    class="text-[#E63946] hover:text-[#C62828] font-medium transition ml-1"
                >
                    {{ $t('Contact Admin') }}
                </a>
            </div>
        </div>
    </form>
</template>

<script>
import Cookies from 'js-cookie';

export default {
    name: "login",
    data() {
        return {
            showPassword: false,
            isLoading: false,
            user: {
                email: null,
                password: null,
                remember: false,
                captcha: null
            }
        }
    },
    computed: {
        welcomeTitle() {
            return this.$store.state.settings.login_welcome_title || this.$t('Welcome!');
        },
        welcomeSubtitle() {
            return this.$store.state.settings.login_welcome_subtitle || this.$t('Enter your information to log in');
        },
        buttonText() {
            return this.$store.state.settings.login_button_text || this.$t('Sign In');
        }
    },
    metaInfo() {
        return {
            title: this.$i18n.t('Sign In')
        }
    },
    methods: {
        submit() {
            const self = this;
            if (self.$store.state.settings.recaptcha_enabled) {
                self.$recaptcha('login').then(function (token) {
                    self.user.captcha = token;
                    self.login();
                });
            } else {
                self.login();
            }
        },
        login() {
            const self = this;
            self.isLoading = true;

            axios.post('api/auth/login', this.user).then(function (response) {
                self.$store.commit('login', response.data);
                const intendedUrl = Cookies.get('intended_url');
                const permissions = (response.data.user && response.data.user.role && response.data.user.role.permissions) ? response.data.user.role.permissions : {};
                const hasTicketAccess = !!permissions['App.Http.Controllers.Api.Dashboard.TicketController'];
                const hasStatsAccess = !!permissions['App.Http.Controllers.Api.Dashboard.StatsController'];
                const defaultRoute = hasTicketAccess ? '/dashboard/tickets' : (hasStatsAccess ? '/dashboard/home' : '/account');

                if (intendedUrl) {
                    Cookies.remove('intended_url');
                    self.$router.push(intendedUrl);
                } else {
                    self.$router.push(defaultRoute);
                }
            }).catch(function () {
                self.user.password = null;
                self.isLoading = false;
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('Invalid credentials').toString(),
                    type: 'error'
                });
            });
        }
    }
}
</script>

<style scoped>
.login-form-container {
    /* Form is styled by parent card */
    position: relative;
}

.logo-container {
    position: absolute;
    top: -60px;
    left: 50%;
    transform: translateX(-50%);
    width: 120px;
    height: 120px;
    background: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
    z-index: 3;
    border: 4px solid white;
}

.logo-image {
    width: 80px;
    height: 80px;
    object-fit: contain;
}

.form-input {
    border-width: 1px;
}

.form-input:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}
</style>
